import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign, TrendingUp, TrendingDown, PieChart,
  FileText, RefreshCw, AlertTriangle, CreditCard, Percent
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import Loading from '../../components/Loading';

const AdminFinancials = () => {
  const [data, setData] = useState(null);
  const [scheduleCData, setScheduleCData] = useState(null);
  const [platformRevenue, setPlatformRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(null);
  const [showScheduleC, setShowScheduleC] = useState(false);

  useEffect(() => {
    loadFinancials();
  }, [year, month]);

  const loadFinancials = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getFinancials({ year, month: month || undefined });
      setData(response.data);

      const startDate = month 
        ? new Date(year, month - 1, 1).toISOString()
        : new Date(year, 0, 1).toISOString();
      const endDate = month
        ? new Date(year, month, 0, 23, 59, 59).toISOString()
        : new Date(year, 11, 31, 23, 59, 59).toISOString();

      try {
        const revenueResponse = await adminApi.getPlatformRevenue(startDate, endDate);
        setPlatformRevenue(revenueResponse.data);
      } catch {
        setPlatformRevenue(null);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load financials');
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleC = async () => {
    try {
      const response = await adminApi.getScheduleC(year);
      setScheduleCData(response.data);
      setShowScheduleC(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load Schedule C data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  const getMonthName = (monthNum) => {
    return new Date(2000, monthNum - 1, 1).toLocaleDateString('en-US', { month: 'short' });
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  const months = [
    { value: null, label: 'Full Year' },
    { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
  ];

  const subscriptionRevenue = data?.netRevenue || 0;
  const paymentFeeRevenue = platformRevenue?.totalRevenue || 0;
  const totalPlatformRevenue = subscriptionRevenue + paymentFeeRevenue;

  if (loading) return <Loading />;

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Platform Financials</h1>
          <p className="text-sm text-gray-600">SuddyFlow P&L for Schedule C</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadScheduleC} className="btn btn-secondary flex items-center gap-2 text-sm">
            <FileText size={16} />
            <span className="hidden sm:inline">Schedule C</span>
            <span className="sm:hidden">Sched C</span>
          </button>
          <button onClick={loadFinancials} className="btn btn-primary flex items-center gap-2 text-sm">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3 sm:p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Period</label>
            <select
              value={month || ''}
              onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              {months.map(m => <option key={m.value || 'year'} value={m.value || ''}>{m.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6 text-sm">{error}</div>
      )}

      {data && !showScheduleC && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="card p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <p className="text-xs text-green-600 font-medium">Total Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-green-900">{formatCurrency(totalPlatformRevenue)}</p>
              <p className="text-xs text-green-600 mt-1">Subs + Fees</p>
            </div>
            <div className="card p-3 sm:p-4 bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
              <p className="text-xs text-red-600 font-medium">Expenses</p>
              <p className="text-lg sm:text-2xl font-bold text-red-900">{formatCurrency(data.totalExpenses)}</p>
            </div>
            <div className={`card p-3 sm:p-4 ${(totalPlatformRevenue - data.totalExpenses) >= 0 
              ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200' 
              : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'}`}>
              <p className={`text-xs font-medium ${(totalPlatformRevenue - data.totalExpenses) >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                Net Profit
              </p>
              <p className={`text-lg sm:text-2xl font-bold ${(totalPlatformRevenue - data.totalExpenses) >= 0 ? 'text-blue-900' : 'text-amber-900'}`}>
                {formatCurrency(totalPlatformRevenue - data.totalExpenses)}
              </p>
            </div>
            <div className="card p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
              <p className="text-xs text-purple-600 font-medium">Margin</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-900">
                {formatPercent(totalPlatformRevenue > 0 ? ((totalPlatformRevenue - data.totalExpenses) / totalPlatformRevenue * 100) : 0)}
              </p>
            </div>
          </div>

          {/* Revenue Breakdown - Stack on mobile */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
            {/* Subscription Revenue */}
            <div className="card p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                <DollarSign size={16} className="text-blue-600" />
                Subscription Revenue
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                    <span className="text-gray-600">Solo ($19)</span>
                  </div>
                  <span className="font-semibold text-gray-900">{formatCurrency(data.soloRevenue)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-gray-600">Pro ($49)</span>
                  </div>
                  <span className="font-semibold text-gray-900">{formatCurrency(data.proRevenue)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-gray-600">Enterprise ($129)</span>
                  </div>
                  <span className="font-semibold text-gray-900">{formatCurrency(data.enterpriseRevenue)}</span>
                </div>
                <div className="pt-2 border-t border-gray-100 text-sm">
                  <div className="flex items-center justify-between text-red-600">
                    <span>Stripe Fees (~2.9%)</span>
                    <span className="font-semibold">-{formatCurrency(data.stripeFeesCollected)}</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">Net Subscription</span>
                    <span className="font-bold text-gray-900">{formatCurrency(data.netRevenue)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Processing Revenue */}
            <div className="card p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                <CreditCard size={16} className="text-green-600" />
                Payment Fees
                <span className="text-xs font-normal text-gray-500 ml-auto">0.5%</span>
              </h3>
              {platformRevenue && platformRevenue.totalTransactions > 0 ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Payment Volume</span>
                    <span className="font-semibold">{formatCurrency(platformRevenue.totalPaymentVolume)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Transactions</span>
                    <span className="font-semibold">{platformRevenue.totalTransactions}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-green-700">Platform Fees</span>
                      <span className="font-bold text-green-700">{formatCurrency(platformRevenue.totalRevenue)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <CreditCard size={24} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">No payment fees yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Expenses by Category */}
          <div className="card p-3 sm:p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">Expenses by Category</h3>
              <Link to="/admin/expenses" className="text-xs text-primary-600 hover:text-primary-700">
                Manage
              </Link>
            </div>
            {data.expensesByCategory.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <p className="mb-2 text-sm">No expenses recorded</p>
                <Link to="/admin/expenses" className="text-primary-600 text-xs">Add expenses →</Link>
              </div>
            ) : (
              <div className="space-y-2">
                {data.expensesByCategory.slice(0, 6).map((cat, index) => (
                  <div key={index} className="flex items-center justify-between py-1 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-gray-400">L{cat.scheduleCLine}</span>
                      <span className="text-gray-600 truncate">{cat.displayName}</span>
                    </div>
                    <span className="font-medium text-gray-900 flex-shrink-0">{formatCurrency(cat.amount)}</span>
                  </div>
                ))}
                {data.expensesByCategory.length > 6 && (
                  <Link to="/admin/expenses" className="block text-xs text-primary-600 pt-2">
                    View all {data.expensesByCategory.length} categories →
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Tax Set-Aside */}
          {(totalPlatformRevenue - data.totalExpenses) > 0 && (
            <div className="card p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-purple-600" size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-purple-900 mb-2 text-sm">Tax Set-Aside</h3>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-purple-600">SE Tax</p>
                      <p className="font-semibold text-purple-900">{formatCurrency(data.estimatedSelfEmploymentTax)}</p>
                    </div>
                    <div>
                      <p className="text-purple-600">Income Tax</p>
                      <p className="font-semibold text-purple-900">{formatCurrency(data.estimatedIncomeTax)}</p>
                    </div>
                    <div>
                      <p className="text-purple-600">Total</p>
                      <p className="font-bold text-purple-900">{formatCurrency(data.recommendedTaxSetAside)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Schedule C View */}
      {showScheduleC && scheduleCData && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Schedule C - {scheduleCData.taxYear}</h2>
            <button onClick={() => setShowScheduleC(false)} className="text-sm text-primary-600">
              ← Back
            </button>
          </div>

          <div className="card p-4">
            <div className="border-b border-gray-200 pb-3 mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">{scheduleCData.businessName}</h3>
              <p className="text-xs text-gray-600">Code: {scheduleCData.principalBusinessCode}</p>
            </div>

            {/* Income */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2 text-sm flex items-center gap-2">
                <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center text-xs text-green-700">I</span>
                Income
              </h4>
              <div className="bg-green-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">L1 - Gross receipts</span>
                  <span className="font-semibold">{formatCurrency(scheduleCData.grossReceipts + paymentFeeRevenue)}</span>
                </div>
                <div className="flex justify-between border-t border-green-200 pt-1 mt-1">
                  <span className="font-medium">L5 - Gross profit</span>
                  <span className="font-bold text-green-700">{formatCurrency(scheduleCData.grossProfit + paymentFeeRevenue)}</span>
                </div>
              </div>
            </div>

            {/* Expenses */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2 text-sm flex items-center gap-2">
                <span className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center text-xs text-red-700">II</span>
                Expenses
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                {scheduleCData.expenseLines.map((line, index) => (
                  <div key={index} className="flex justify-between py-0.5">
                    <span className="text-gray-600 truncate flex-1 mr-2">L{line.lineNumber} - {line.description}</span>
                    <span className="font-semibold flex-shrink-0">{formatCurrency(line.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                  <span className="font-medium">L28 - Total</span>
                  <span className="font-bold text-red-700">{formatCurrency(scheduleCData.totalExpenses)}</span>
                </div>
              </div>
            </div>

            {/* Net Profit */}
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="font-medium">L31 - Net profit</span>
                <span className={`font-bold text-lg ${(scheduleCData.netProfit + paymentFeeRevenue) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {formatCurrency(scheduleCData.netProfit + paymentFeeRevenue)}
                </span>
              </div>
            </div>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Disclaimer:</strong> Preview only. Consult a tax professional.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFinancials;